/**
 * Minimalerei Eligibility-Cards — Phase 3.E.2
 *
 * <eligibility-cards> Custom Element. Two-column layout: an item-list
 * left, a stacked image-column right (or left, controlled by a parent
 * data-attr). Hover/focus/tap on a list-item activates it AND cross-
 * fades the corresponding image. Optional auto-cycle that pauses on
 * hover.
 *
 * Image-stack synthesis: theme-block sub-blocks can only render at
 * one DOM position via {% content_for "blocks" %}, so the children
 * render as <li> only. We read each item's data-image-* / data-
 * caption-* attributes here and build the matching <figure>+<img>+
 * <figcaption> elements into the empty image-stack container.
 *
 * The first item is marked active here (not in Liquid) because
 * children rendered via content_for don't have a forloop context.
 */
class EligibilityCards extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.autoCycleTimer = null;
  }

  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('.eligibility__item'));
    this.stack = this.querySelector('[data-image-stack]');
    if (!this.items.length || !this.stack) return;

    this.buildImageStack();
    this.images = Array.from(this.stack.querySelectorAll('.eligibility__image-item'));

    this.markFirstActive();

    this.autoCycle = this.dataset.autoCycle === 'true';
    this.cycleInterval = parseInt(this.dataset.cycleInterval) || 4000;

    this.attachEventListeners();
    if (this.autoCycle) this.startAutoCycle();
  }

  disconnectedCallback() {
    this.stopAutoCycle();
  }

  /**
   * Reads each item's data-* and creates one <figure> per item in the
   * stack. Caption-icon is cloned from the item's icon span so the
   * SVG markup matches whatever the Liquid `icon` snippet produced —
   * no need to duplicate the SVG in JS.
   */
  buildImageStack() {
    this.items.forEach((item, i) => {
      const figure = document.createElement('figure');
      figure.className = 'eligibility__image-item';
      figure.dataset.imageIndex = i;
      figure.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');

      const src = item.dataset.imageSrc;
      if (src) {
        const img = document.createElement('img');
        img.className = 'eligibility__image';
        img.src = src;
        if (item.dataset.imageSrcset) img.srcset = item.dataset.imageSrcset;
        img.sizes = '(max-width: 768px) 90vw, 50vw';
        img.alt = item.dataset.imageAlt || '';
        img.loading = 'lazy';
        if (item.dataset.imageWidth) img.width = item.dataset.imageWidth;
        if (item.dataset.imageHeight) img.height = item.dataset.imageHeight;
        figure.appendChild(img);
      }

      const caption = document.createElement('figcaption');
      caption.className = 'eligibility__caption';

      const sourceIcon = item.querySelector('.eligibility__item-icon');
      if (sourceIcon) {
        const captionIcon = sourceIcon.cloneNode(true);
        captionIcon.className = 'eligibility__caption-icon';
        caption.appendChild(captionIcon);
      }

      const captionContent = document.createElement('span');
      captionContent.className = 'eligibility__caption-content';
      const titleEl = document.createElement('span');
      titleEl.className = 'eligibility__caption-title';
      titleEl.textContent = item.dataset.captionTitle || '';
      captionContent.appendChild(titleEl);
      if (item.dataset.captionDesc) {
        const descEl = document.createElement('span');
        descEl.className = 'eligibility__caption-desc';
        descEl.textContent = item.dataset.captionDesc;
        captionContent.appendChild(descEl);
      }
      caption.appendChild(captionContent);
      figure.appendChild(caption);

      this.stack.appendChild(figure);
    });
  }

  markFirstActive() {
    this.items[0]?.classList.add('is-active');
    this.items[0]?.setAttribute('aria-pressed', 'true');
    this.stack.querySelector('.eligibility__image-item')?.classList.add('is-active');
  }

  attachEventListeners() {
    this.items.forEach((item, index) => {
      item.addEventListener('mouseenter', () => {
        this.setActive(index);
        this.stopAutoCycle();
      });

      item.addEventListener('click', () => {
        this.setActive(index);
        this.stopAutoCycle();
      });

      item.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.setActive(index);
          this.stopAutoCycle();
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          this.focusNext(index);
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          this.focusPrev(index);
        }
      });

      item.addEventListener('focus', () => this.setActive(index));
    });

    this.addEventListener('mouseenter', () => this.stopAutoCycle());
    this.addEventListener('mouseleave', () => {
      if (this.autoCycle) this.startAutoCycle();
    });
  }

  setActive(index) {
    if (index === this.activeIndex) return;

    this.items[this.activeIndex]?.classList.remove('is-active');
    this.items[this.activeIndex]?.setAttribute('aria-pressed', 'false');
    this.images[this.activeIndex]?.classList.remove('is-active');
    this.images[this.activeIndex]?.setAttribute('aria-hidden', 'true');

    this.activeIndex = index;

    this.items[index]?.classList.add('is-active');
    this.items[index]?.setAttribute('aria-pressed', 'true');
    this.images[index]?.classList.add('is-active');
    this.images[index]?.setAttribute('aria-hidden', 'false');
  }

  focusNext(currentIndex) {
    const next = (currentIndex + 1) % this.items.length;
    this.items[next].focus();
  }

  focusPrev(currentIndex) {
    const prev = (currentIndex - 1 + this.items.length) % this.items.length;
    this.items[prev].focus();
  }

  startAutoCycle() {
    this.stopAutoCycle();
    this.autoCycleTimer = setInterval(() => {
      const nextIndex = (this.activeIndex + 1) % this.items.length;
      this.setActive(nextIndex);
    }, this.cycleInterval);
  }

  stopAutoCycle() {
    if (this.autoCycleTimer) {
      clearInterval(this.autoCycleTimer);
      this.autoCycleTimer = null;
    }
  }
}

if (!customElements.get('eligibility-cards')) {
  customElements.define('eligibility-cards', EligibilityCards);
}
